import {
  applyThreeDSFrameSize,
  createThreeDSFrameShell,
  createThreeDSModalBackdrop,
  normalizeThreeDSFrameSize,
  type ThreeDSFrameShell,
  type ThreeDSFrameSize,
} from './threeds-modal-sizing';

export type ThreeDSFlowData = {
  sessionId: string;
  step: 'challenge' | 'decoupled_waiting';
  challengeUrl?: string;
  acsTransactionId?: string;
  threeDSVersion?: string;
  cardBrand?: string;
  liveMode?: boolean;
};

type ThreeDSModalControllerOptions<TData extends ThreeDSFlowData> = {
  modalId: string;
  buildUrl: (data: TData) => string;
  onCancel: () => void;
  onReady: (iframe: HTMLIFrameElement, data: TData) => void;
};

export class ThreeDSModalController<TData extends ThreeDSFlowData = ThreeDSFlowData> {
  private modal: HTMLDivElement | null = null;
  private shell: ThreeDSFrameShell | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private frameSize: ThreeDSFrameSize = {};
  private resizeHandler: (() => void) | null = null;
  private pendingData: TData | null = null;

  constructor(private readonly options: ThreeDSModalControllerOptions<TData>) {}

  show(data: TData): void {
    this.close();

    this.modal = createThreeDSModalBackdrop(this.options.modalId);
    this.shell = createThreeDSFrameShell(() => this.options.onCancel());
    this.iframe = this.shell.iframe;
    this.iframe.src = this.options.buildUrl(data);
    this.frameSize = {};

    this.applySize();

    this.modal.appendChild(this.shell.shell);
    document.body.appendChild(this.modal);

    this.resizeHandler = () => this.applySize();
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('orientationchange', this.resizeHandler);
    window.visualViewport?.addEventListener('resize', this.resizeHandler);

    this.pendingData = data;
  }

  handleFrameResize(evt: MessageEvent, data: unknown): void {
    if (!this.ownsMessageSource(evt.source)) {
      return;
    }

    const nextSize = normalizeThreeDSFrameSize(data);
    if (!nextSize) {
      return;
    }

    this.frameSize = {
      ...this.frameSize,
      ...nextSize,
    };
    this.applySize();
  }

  ownsMessageSource(source: MessageEventSource | null): boolean {
    return Boolean(this.iframe?.contentWindow && source === this.iframe.contentWindow);
  }

  handleReady(): boolean {
    if (!this.pendingData || !this.iframe?.contentWindow) {
      return false;
    }

    this.options.onReady(this.iframe, this.pendingData);
    return true;
  }

  close(): void {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('orientationchange', this.resizeHandler);
      window.visualViewport?.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    if (this.modal) {
      this.modal.remove();
      this.modal = null;
    }

    this.shell = null;
    this.iframe = null;
    this.frameSize = {};
    this.pendingData = null;
  }

  private applySize(): void {
    applyThreeDSFrameSize(this.modal, this.iframe, this.frameSize, this.shell?.shell);
  }
}
